import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Quote, ChevronLeft, ChevronRight, Calendar, Users, MessageSquare } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';
import lecturesPhoto from '../assets/lectures.png';

const LecturesSection = () => {
  const { lecturesData } = useLanguage();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const handleNavClick = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => 
      prev === lecturesData.testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) =>
      prev === 0 ? lecturesData.testimonials.length - 1 : prev - 1
    );
  };

  // Auto-rotate testimonials every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial((prev) =>
        prev === lecturesData.testimonials.length - 1 ? 0 : prev + 1
      );
    }, 4000);
    return () => clearInterval(timer);
  }, [lecturesData.testimonials.length]);

  return (
    <section
      id="lectures"
      className="py-20 md:py-32 relative"
      style={{ background: 'var(--bg-section)', overflow: 'hidden' }}
    >
      {/* Background Photo - Right Side - ABOVE everything */}
      <div className="absolute right-0 top-0 bottom-0 hidden xl:block" style={{ width: '45%', overflow: 'hidden', zIndex: 30 }}>
        {/* Left fade to blend with content */}
        <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '35%', background: 'linear-gradient(to right, var(--bg-section), transparent)', zIndex: 2 }} />
        {/* Bottom fade */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '80px', background: 'linear-gradient(to top, var(--bg-section), transparent)', zIndex: 2 }} />
        <motion.img
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          src={lecturesPhoto}
          alt="Lucimar Bizoni palestrando"
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            height: '98%',
            width: 'auto',
            objectFit: 'contain',
            zIndex: 1,
            WebkitMaskImage: 'radial-gradient(ellipse 90% 95% at 60% 50%, black 60%, transparent 100%)',
            maskImage: 'radial-gradient(ellipse 90% 95% at 60% 50%, black 60%, transparent 100%)'
          }}
        />
      </div>

      <div className="container relative z-20">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 xl:text-left xl:max-w-xl"
        >
          <span className="mono-text mb-4 block" style={{ color: 'var(--accent-gold-400)' }}>
            EVENTOS
          </span>
          <h2 className="heading-hero mb-4">{lecturesData.title}</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            {lecturesData.subtitle}
          </p>
        </motion.div>

        {/* Content Grid - apenas coluna esquerda, Depoimentos vai absoluto */}
        <div className="xl:max-w-lg space-y-6">
          {/* Themes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="voice-card"
            style={{ background: 'var(--bg-card)' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'var(--accent-gold-200)' }}
              >
                <MessageSquare size={18} style={{ color: 'var(--accent-gold-400)' }} />
              </div>
              <h3 className="heading-3">Temas</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {lecturesData.themes.map((theme, index) => (
                <span key={index} className="btn-tag">{theme}</span>
              ))}
            </div>
          </motion.div>

          {/* Audiences */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="voice-card accent-bronze"
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(255,255,255,0.6)' }}
              >
                <Users size={18} style={{ color: 'var(--text-primary)' }} />
              </div>
              <h3 className="heading-3">Público Atendido</h3>
            </div>
            <ul className="space-y-2">
              {lecturesData.audiences.map((audience, index) => (
                <li key={index} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--accent-bronze-400)' }} />
                  <span className="body-small">{audience}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <button
              onClick={() => handleNavClick('#contact')}
              className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2"
            >
              <Calendar size={18} />
              Agende uma Palestra
            </button>
          </motion.div>
        </div>

        {/* Depoimentos - absolutamente posicionado na frente da foto, na altura da cintura */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{
            position: 'absolute',
            bottom: '80px',
            right: '40px',
            width: '300px',
            zIndex: 40,
          }}
        >
          <div className="voice-card flex flex-col shadow-2xl" style={{ background: 'var(--bg-card)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'var(--accent-rose-200)' }}
              >
                <Quote size={18} style={{ color: 'var(--accent-rose-400)' }} />
              </div>
              <h3 className="heading-3">Depoimentos</h3>
            </div>

            <motion.div
              key={activeTestimonial}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <p className="text-base mb-4" style={{ color: 'var(--text-secondary)', fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', lineHeight: 1.7 }}>
                "{lecturesData.testimonials[activeTestimonial].quote}"
              </p>
              <p className="heading-3" style={{ marginBottom: '0.2rem' }}>
                {lecturesData.testimonials[activeTestimonial].author}
              </p>
              <p className="caption">{lecturesData.testimonials[activeTestimonial].role}</p>
            </motion.div>

            <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: '1px solid var(--border-light)' }}>
              <div className="flex gap-2">
                {lecturesData.testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index)}
                    className={`h-1.5 rounded-full transition-all duration-200 ${index === activeTestimonial ? 'w-8' : 'w-1.5'}`}
                    style={{ background: index === activeTestimonial ? 'var(--accent-gold-400)' : 'var(--border-light)' }}
                    aria-label={`Depoimento ${index + 1}`}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={prevTestimonial} className="btn-nav" aria-label="Anterior"><ChevronLeft size={16} /></button>
                <button onClick={nextTestimonial} className="btn-nav" aria-label="Próximo"><ChevronRight size={16} /></button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Depoimentos - direto na seção, na altura da cintura/quadril da foto */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="hidden xl:block"
        style={{
          position: 'absolute',
          bottom: '6%',
          right: '5%',
          width: '290px',
          zIndex: 40,
        }}
      >
        <div className="voice-card flex flex-col shadow-2xl" style={{ background: 'var(--bg-card)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'var(--accent-rose-200)' }}
            >
              <Quote size={18} style={{ color: 'var(--accent-rose-400)' }} />
            </div>
            <h3 className="heading-3">Depoimentos</h3>
          </div>

          <motion.div
            key={activeTestimonial}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-base mb-4" style={{ color: 'var(--text-secondary)', fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', lineHeight: 1.7 }}>
              &ldquo;{lecturesData.testimonials[activeTestimonial].quote}&rdquo;
            </p>
            <p className="heading-3" style={{ marginBottom: '0.2rem' }}>
              {lecturesData.testimonials[activeTestimonial].author}
            </p>
            <p className="caption">{lecturesData.testimonials[activeTestimonial].role}</p>
          </motion.div>

          <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: '1px solid var(--border-light)' }}>
            <div className="flex gap-2">
              {lecturesData.testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`h-1.5 rounded-full transition-all duration-200 ${index === activeTestimonial ? 'w-8' : 'w-1.5'}`}
                  style={{ background: index === activeTestimonial ? 'var(--accent-gold-400)' : 'var(--border-light)' }}
                  aria-label={`Depoimento ${index + 1}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={prevTestimonial} className="btn-nav" aria-label="Anterior"><ChevronLeft size={16} /></button>
              <button onClick={nextTestimonial} className="btn-nav" aria-label="Próximo"><ChevronRight size={16} /></button>
            </div>
          </div>
        </div>
      </motion.div>

    </section>
  );
};

export default LecturesSection;
